import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import PaymentMethodDisplay from '../components/PaymentMethodDisplay';
import './OrderDetail.css';

const OrderDetail = () => {
  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: '',
  });

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/user/orders/${id}`);
      setOrder(response);
    } catch (error) {
      toast.error('Order not found');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrder();
      if (window.location.hash === '#review') {
        setShowReviewModal(true);
      }
    }
  }, [isAuthenticated, fetchOrder]);

  const handleReviewSubmit = async (productId) => {
    try {
      await api.post('/reviews', {
        product_id: productId,
        order_id: id,
        rating: reviewForm.rating,
        review_text: reviewForm.review_text,
      });
      toast.success('Review submitted successfully');
      setShowReviewModal(false);
      setReviewForm({ rating: 5, review_text: '' });
      setSelectedProduct(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit review');
    }
  };

  const openReviewModal = (product) => {
    setSelectedProduct(product);
    setShowReviewModal(true);
  };

  // Refund/return-related statuses (exclude cancelled): show filled until delivery + refund block + View Return
  const REFUND_STATUSES = ['refund_initialized', 'refunded', 'returned'];
  const isRefundStatus = REFUND_STATUSES.includes(order?.order_status);

  // Map order status to tracking stages (when refund status: fill until delivery only)
  const getTrackingStages = () => {
    const stages = [
      { key: 'ordered', label: 'Ordered', icon: '✓' },
      { key: 'confirmed', label: 'Confirmed', icon: '✓' },
      { key: 'processing', label: 'Processing', icon: '✓' },
      { key: 'shipped', label: 'Shipped', icon: '✓' },
      { key: 'delivered', label: 'Delivered', icon: '✓' },
    ];

    // Map backend statuses to tracking stages
    const statusMap = {
      pending: 'ordered',
      confirmed: 'confirmed',
      processing: 'processing',
      shipped: 'shipped',
      delivered: 'delivered',
      cancelled: 'cancelled',
      order_cancelled: 'cancelled',
      refund_initialized: 'delivered',
      refunded: 'delivered',
      returned: 'delivered',
    };

    // When refund status: show all steps up to (and including) delivery as completed
    const effectiveStatus = isRefundStatus ? 'delivered' : (statusMap[order?.order_status] || 'ordered');
    const currentStatus = effectiveStatus;
    const currentIndex = stages.findIndex((s) => s.key === currentStatus);

    return stages.map((stage, index) => {
      const isCompleted = index <= currentIndex;
      const isCurrent = !isRefundStatus && index === currentIndex;
      return {
        ...stage,
        isCompleted,
        isCurrent,
      };
    });
  };

  // Refund by admin with no client return request – show amount and admin message
  const isAdminRefundNoReturn =
    (order?.order_status === 'refund_initialized' || order?.order_status === 'refunded') &&
    !order?.return_id;

  // Message for refund block
  const getRefundStatusMessage = () => {
    const s = order?.order_status;
    if (s === 'cancelled' || s === 'order_cancelled') return 'This order was cancelled.';
    if (isAdminRefundNoReturn) {
      const amount = (parseFloat(order?.amount_refund) || 0).toFixed(2);
      if (s === 'refunded') {
        return ` ₹${amount} Refunded by admin (refund initialized by admin).`;
      }
      return `Refund initialized by admin. You will get a refund of ₹${amount}.`;
    }
    return 'This order has returned items.';
  };

  // Order is cancelled: show Order Cancelled card only (no progress bar)
  const isCancelled =
    order?.order_status === 'cancelled' || order?.order_status === 'order_cancelled';

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.png';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('/')) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const baseUrl = API_BASE_URL.replace(/\/api$/, '');
      return `${baseUrl}${imagePath}`;
    }
    return imagePath;
  };

  // Get tracking number and carrier from tracking history
  const getTrackingInfo = () => {
    if (!order?.tracking || order.tracking.length === 0) {
      return {
        trackingNumber: null,
        carrier: null,
        trackingImage: null,
        trackingLink: null,
        notes: null,
        isShipped: false,
        latestTracking: null,
      };
    }

    // Show tracking section when order has reached shipped, delivered, or any refund-related status
    const isShipped =
      order.order_status === 'shipped' ||
      order.order_status === 'delivered' ||
      isRefundStatus;

    // Prefer an entry that has tracking_number/carrier (often on "shipped"), then fallback to first shipped/delivered
    const shippedOrDelivered = order.tracking.filter(
      (t) => t.status === 'shipped' || t.status === 'delivered'
    );
    const withTracking = shippedOrDelivered.find(
      (t) => t.tracking_number || t.tracking_link || t.carrier
    );
    const shippedTracking =
      withTracking ||
      shippedOrDelivered[0] ||
      order.tracking[order.tracking.length - 1];

    // Get latest tracking entry for non-shipped orders
    const latestTracking = order.tracking[order.tracking.length - 1];

    const trackingNumber = shippedTracking?.tracking_number || null;
    const isTrackingUrl =
      trackingNumber &&
      (trackingNumber.startsWith('http://') || trackingNumber.startsWith('https://'));

    return {
      trackingNumber: trackingNumber,
      carrier: shippedTracking?.carrier || null,
      trackingImage: isTrackingUrl ? trackingNumber : null,
      isTrackingUrl: isTrackingUrl,
      trackingLink: shippedTracking?.tracking_link || null,
      notes: shippedTracking?.notes || null,
      isShipped: isShipped,
      latestTracking: latestTracking,
    };
  };

  if (loading) return null;

  if (!order) return null;

  const trackingStages = getTrackingStages();
  const trackingInfo = getTrackingInfo();
  const orderDate = new Date(order.created_at).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="order-detail-page">
      <Container>
        {/* Breadcrumbs */}
        <nav className="breadcrumb-nav" aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/orders">Order</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Track Order
            </li>
          </ol>
        </nav>

        {/* Order Status: show dedicated card for cancelled or admin refund (no return); else progress bar */}
        {isCancelled ? (
          <div className="order-status-section cancelled-status">
            <div className="cancelled-message">
              <div className="cancelled-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#dc3545" stroke="#dc3545" strokeWidth="2" />
                  <path
                    d="M15 9l-6 6M9 9l6 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="cancelled-title">Order Cancelled</h3>
              <p className="cancelled-text">This order has been cancelled.</p>
            </div>
          </div>
        ) : isAdminRefundNoReturn ? (
          <div className="order-status-section refund-status-card">
            <div className="refund-message">
              <div className="refund-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48px" height="48px"><path fill="#2196f3" d="M44,24c0,11.045-8.955,20-20,20S4,35.045,4,24S12.955,4,24,4S44,12.955,44,24z"/><path fill="#fff" d="M22 22h4v11h-4V22zM26.5 16.5c0 1.379-1.121 2.5-2.5 2.5s-2.5-1.121-2.5-2.5S22.621 14 24 14 26.5 15.121 26.5 16.5z"/></svg>
              </div>
              <h3 className="refund-title">
                {order?.order_status === 'refunded' ? 'Refunded' : 'Refund Initialized'}
              </h3>
              <p className="refund-text">{getRefundStatusMessage()}</p>
            </div>
          </div>
        ) : (
          <div className="order-status-section">
            <h2 className="order-status-title">Order Status</h2>
            <div className="status-progress-bar">
              {trackingStages.map((stage, index) => (
                <React.Fragment key={stage.key}>
                  <div className="status-stage">
                    <div
                      className={`status-icon ${stage.isCompleted ? 'completed' : ''} ${
                        stage.isCurrent ? 'current' : ''
                      }`}
                    >
                      {stage.isCompleted ? (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <div className="status-icon-placeholder"></div>
                      )}
                    </div>
                    <div className="status-label">{stage.label}</div>
                  </div>
                  {index < trackingStages.length - 1 && (
                    <div className={`status-line ${stage.isCompleted ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {isRefundStatus && (
              <div className="order-refund-status-block">
                <p className="order-refund-status-message">{getRefundStatusMessage()}</p>
                {!isAdminRefundNoReturn && (
                  <Link
                    to={order.return_id ? `/returns/detail/${order.return_id}` : '/returns'}
                    className="order-view-return-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    View Return
                  </Link>
                )}
              </div>
            )}
          </div>
        )}

        <Row className="order-content-row">
          {/* Left Column - Order Summary */}
          <Col lg={6} md={6} className="order-summary-col">
            <Card className="order-summary-card">
              <Card.Body>
                <h3 className="order-summary-title">Order Summary</h3>
                <div className="order-summary-item">
                  <span className="summary-label">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="13"
                      viewBox="0 0 11 13"
                      fill="none"
                    >
                      <path
                        d="M7.33333 0H1.22222C0.898069 0 0.587192 0.152182 0.357981 0.423068C0.128769 0.693954 0 1.06135 0 1.44444V11.5556C0 11.9386 0.128769 12.306 0.357981 12.5769C0.587192 12.8478 0.898069 13 1.22222 13H9.77778C10.1019 13 10.4128 12.8478 10.642 12.5769C10.8712 12.306 11 11.9386 11 11.5556V4.33333L7.33333 0ZM9.77778 11.5556H1.22222V1.44444H6.72222V5.05556H9.77778M8.55556 7.94444H2.44444V6.5H8.55556M6.72222 10.1111H2.44444V8.66667H6.72222"
                        fill="black"
                      />
                    </svg>
                    Order Number:
                  </span>
                  <span className="summary-value">#{order.order_number}</span>
                </div>
                <div className="order-summary-item">
                  <span className="summary-label">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="9"
                      height="9"
                      viewBox="0 0 9 9"
                      fill="none"
                    >
                      <path
                        d="M3.28947 5.0625V3.9375H12.5V5.0625H3.28947ZM3.28947 8.4375V7.3125H12.5V8.4375H3.28947ZM3.28947 1.6875V0.5625H12.5V1.6875H3.28947ZM0.657895 2.25V0.5625H0V0H1.31579V2.25H0.657895ZM0 7.3125V6.75H1.97368V9H0V8.4375H1.31579V8.15625H0.657895V7.59375H1.31579V7.3125H0ZM1.48026 3.375C1.61113 3.375 1.73663 3.41945 1.82916 3.49856C1.9217 3.57768 1.97368 3.68499 1.97368 3.79688C1.97368 3.90937 1.92105 4.01625 1.83553 4.08938L0.736842 5.0625H1.97368V5.625H0V5.1075L1.31579 3.9375H0V3.375H1.48026Z"
                        fill="black"
                      />
                    </svg>
                    Number Of Items:
                  </span>
                  <span className="summary-value">{order.items?.length || 0}</span>
                </div>
                <div className="order-summary-item">
                  <span className="summary-label">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                    >
                      <path
                        d="M2.88889 6.5H4.33333V7.8H2.88889V6.5ZM13 2.6V11.7C13 12.4215 12.3572 13 11.5556 13H1.44444C0.642778 13 0 12.415 0 11.7V2.6C0 1.885 0.65 1.3 1.44444 1.3H2.16667V0H3.61111V1.3H9.38889V0H10.8333V1.3H11.5556C12.3572 1.3 13 1.885 13 2.6ZM1.44444 3.9H11.5556V2.6H1.44444V3.9ZM11.5556 11.7V5.2H1.44444V11.7H11.5556ZM8.66667 7.8H10.1111V6.5H8.66667V7.8ZM5.77778 7.8H7.22222V6.5H5.77778V7.8Z"
                        fill="black"
                      />
                    </svg>
                    Order Date:
                  </span>
                  <span className="summary-value">{orderDate}</span>
                </div>
                <div className="order-summary-item total justify-between">
                  <span className="summary-label">Total:</span>
                  <span className="summary-value">
                    ₹{(parseFloat(order.total_amount) || 0).toFixed(2)}
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} md={6} className="order-tracking-col">
            {/* Tracking Information */}
            <Card className="tracking-info-card">
            <Card.Body>
                <h3 className="tracking-info-title">Tracking Information</h3>
                <Row className="tracking-content-row">
                  <Col lg={trackingInfo.isShipped ? 6 : 12} md={12} className="tracking-left-col">
                    <div className="tracking-content-wrapper">
                      {isCancelled ? (
                        <div className="tracking-info-message tracking-unavailable">
                          <p>Order cancelled. Tracking is unavailable.</p>
                        </div>
                      ) : trackingInfo.isShipped ? (
                        <>
                          {trackingInfo.carrier && (
                            <div className="tracking-info-item">
                              <span className="tracking-label">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="10"
                                  viewBox="0 0 14 10"
                                  fill="none"
                                >
                                  <path
                                    d="M10.8182 9.0625C10.565 9.0625 10.3222 8.96373 10.1432 8.78791C9.9642 8.6121 9.86364 8.37364 9.86364 8.125C9.86364 7.87636 9.9642 7.6379 10.1432 7.46209C10.3222 7.28627 10.565 7.1875 10.8182 7.1875C11.0713 7.1875 11.3141 7.28627 11.4931 7.46209C11.6722 7.6379 11.7727 7.87636 11.7727 8.125C11.7727 8.37364 11.6722 8.6121 11.4931 8.78791C11.3141 8.96373 11.0713 9.0625 10.8182 9.0625ZM11.7727 3.4375L13.02 5H10.1818V3.4375M3.18182 9.0625C2.92866 9.0625 2.68586 8.96373 2.50685 8.78791C2.32784 8.6121 2.22727 8.37364 2.22727 8.125C2.22727 7.87636 2.32784 7.6379 2.50685 7.46209C2.68586 7.28627 2.92866 7.1875 3.18182 7.1875C3.43498 7.1875 3.67777 7.28627 3.85678 7.46209C4.0358 7.6379 4.13636 7.87636 4.13636 8.125C4.13636 8.37364 4.0358 8.6121 3.85678 8.78791C3.67777 8.96373 3.43498 9.0625 3.18182 9.0625ZM12.0909 2.5H10.1818V0H1.27273C0.566364 0 0 0.55625 0 1.25V8.125H1.27273C1.27273 8.62228 1.47386 9.09919 1.83189 9.45083C2.18991 9.80246 2.6755 10 3.18182 10C3.68814 10 4.17373 9.80246 4.53175 9.45083C4.88977 9.09919 5.09091 8.62228 5.09091 8.125H8.90909C8.90909 8.62228 9.11023 9.09919 9.46825 9.45083C9.82627 9.80246 10.3119 10 10.8182 10C11.3245 10 11.8101 9.80246 12.1681 9.45083C12.5261 9.09919 12.7273 8.62228 12.7273 8.125H14V5L12.0909 2.5Z"
                                    fill="black"
                                  />
                                </svg>
                                Courier - Service Provider:
                              </span>
                              <span className="tracking-value">{trackingInfo.carrier}</span>
                            </div>
                          )}
                          {trackingInfo.trackingNumber && (
                            <div className="tracking-info-item">
                              <span className="tracking-label">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="10"
                                  height="13"
                                  viewBox="0 0 10 13"
                                  fill="none"
                                >
                                  <path
                                    d="M5 1.2381C6.375 1.2381 7.5 2.35238 7.5 3.71429C7.5 5.01429 6.1875 7.11905 5 8.60476C3.8125 7.05714 2.5 5.01429 2.5 3.71429C2.5 2.35238 3.625 1.2381 5 1.2381ZM5 0C2.9375 0 1.25 1.67143 1.25 3.71429C1.25 6.5 5 10.5238 5 10.5238C5 10.5238 8.75 6.4381 8.75 3.71429C8.75 1.67143 7.0625 0 5 0ZM5 2.47619C4.3125 2.47619 3.75 3.03333 3.75 3.71429C3.75 4.39524 4.3125 4.95238 5 4.95238C5.6875 4.95238 6.25 4.39524 6.25 3.71429C6.25 3.03333 5.6875 2.47619 5 2.47619ZM10 10.5238C10 11.8857 7.75 13 5 13C2.25 13 0 11.8857 0 10.5238C0 9.71905 0.75 9.0381 1.9375 8.54286L2.3125 9.1C1.6875 9.40953 1.25 9.78095 1.25 10.2143C1.25 11.081 2.9375 11.7619 5 11.7619C7.0625 11.7619 8.75 11.081 8.75 10.2143C8.75 9.78095 8.3125 9.40953 7.625 9.1L8 8.54286C9.25 9.0381 10 9.71905 10 10.5238Z"
                                    fill="black"
                                  />
                                </svg>
                              Tracking ID And Other Details:
                              </span>

                              {
                                trackingInfo.isTrackingUrl ? <span className="tracking-value">Refer The Image Provided</span> : <span className="tracking-value">{trackingInfo.trackingNumber}</span>
                              }
                      </div>
                          )}
                          {trackingInfo.trackingNumber && (
                            <Button
                              variant="dark"
                              className="track-shipment-btn flex"
                              onClick={() => {
                                if (trackingInfo.trackingLink) {
                                  window.open(trackingInfo.trackingLink, '_blank', 'noopener,noreferrer');
                                }
                              }}
                              disabled={!trackingInfo.trackingLink}
                              style={{
                                cursor: trackingInfo.trackingLink ? 'pointer' : 'not-allowed',
                                opacity: trackingInfo.trackingLink ? 1 : 0.6,
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="16"
                                viewBox="0 0 14 16"
                                fill="none"
                              >
                                <path
                                  d="M6.22222 0C2.78444 0 0 0.4 0 3.2V11.2C0.00175127 11.5355 0.0718738 11.8669 0.205842 12.1728C0.33981 12.4787 0.534646 12.7523 0.777778 12.976V14.4C0.777778 14.6122 0.859722 14.8157 1.00558 14.9657C1.15145 15.1157 1.34928 15.2 1.55556 15.2H2.33333C2.53961 15.2 2.73744 15.1157 2.88331 14.9657C3.02917 14.8157 3.11111 14.6122 3.11111 14.4V13.6H7.77778C7.31278 12.7405 7.04663 11.7824 7 10.8C7.00693 10.0916 7.17857 9.39511 7.50061 8.76883C7.82264 8.14255 8.2857 7.60463 8.85111 7.2H1.55556V3.2H10.8889V6.4C11.0184 6.3918 11.1483 6.3918 11.2778 6.4C11.6728 6.4013 12.0657 6.46056 12.4444 6.576V3.2C12.4444 0.4 9.66 0 6.22222 0ZM2.72222 9.6C2.95297 9.6 3.17853 9.67038 3.37039 9.80224C3.56224 9.93409 3.71178 10.1215 3.80008 10.3408C3.88838 10.5601 3.91149 10.8013 3.86647 11.0341C3.82146 11.2669 3.71034 11.4807 3.54718 11.6485C3.38402 11.8164 3.17614 11.9306 2.94983 11.9769C2.72352 12.0232 2.48894 11.9995 2.27576 11.9087C2.06258 11.8178 1.88037 11.664 1.75217 11.4667C1.62398 11.2693 1.55556 11.0373 1.55556 10.8C1.55556 10.4817 1.67847 10.1765 1.89726 9.95147C2.11606 9.72643 2.4128 9.6 2.72222 9.6ZM11.2778 8C10.5583 8.00832 9.87059 8.30598 9.36182 8.8293C8.85304 9.3526 8.56364 10.06 8.55556 10.8C8.55556 12.88 11.2778 16 11.2778 16C11.2778 16 14 12.88 14 10.8C13.9919 10.06 13.7025 9.3526 13.1937 8.8293C12.685 8.30598 11.9972 8.00832 11.2778 8ZM11.2778 11.84C11.0302 11.84 10.7928 11.7389 10.6178 11.5588C10.4428 11.3788 10.3444 11.1346 10.3444 10.88C10.3444 10.6254 10.4428 10.3812 10.6178 10.2012C10.7928 10.0211 11.0302 9.92 11.2778 9.92C11.5199 9.93685 11.7479 10.0434 11.9195 10.2199C12.0911 10.3964 12.1947 10.6309 12.2111 10.88C12.2166 11.0076 12.1963 11.1349 12.1513 11.254C12.1064 11.373 12.0378 11.4811 11.95 11.5714C11.8622 11.6617 11.7571 11.7323 11.6414 11.7785C11.5256 11.8247 11.4018 11.8457 11.2778 11.84Z"
                                  fill="white"
                                />
                              </svg>
                              Track Shipment
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {trackingInfo.latestTracking && (
                            <>
                              <div className="tracking-info-item">
                                <span className="tracking-label">Status:</span>
                                <span className="tracking-value">
                                  {trackingInfo.latestTracking.status.charAt(0).toUpperCase() +
                                    trackingInfo.latestTracking.status.slice(1).replace('_', ' ')}
                                </span>
                              </div>
                              {trackingInfo.latestTracking.notes && (
                                <div className="tracking-info-item">
                                  <span className="tracking-label">Notes:</span>
                                  <span className="tracking-value">
                                    {trackingInfo.latestTracking.notes}
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          <div className="tracking-info-message">
                            <p>
                              Once status is shipped, tracking information will be displayed here.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                  {trackingInfo.isShipped && trackingInfo.trackingImage && (
                    <Col lg={6} md={12} className="tracking-right-col">
                      <div className="tracking-image-wrapper">
                        <img
                          src={getImageUrl(trackingInfo.trackingImage)}
                          alt="Tracking Label"
                          className="tracking-label-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const textElement = e.target.nextElementSibling;
                            if (textElement) {
                              textElement.style.display = 'block';
                            }
                          }}
                        />
                        <p className="tracking-number-text" style={{ display: 'none' }}>
                          {trackingInfo.trackingNumber.replace(/^https?:\/\//, '')}
                        </p>
                      </div>
                    </Col>
                  )}
                </Row>
            </Card.Body>
          </Card>
          </Col>

          {/* Right Column - Items and Details */}
          <Col lg={12} md={6} className="order-items-col lg:mt-6">
            {/* Items In Your Order */}
            <Card className="order-items-card">
              <Card.Body>
                <h3 className="section-title">Items In Your Order</h3>
                <div className="order-items-list">
                  {order.items?.map((item) => {
                    const isCombo = item.item_type === 'combo' || !!item.combo_id;
                    const comboDetails = item.combo_selections_detail || [];
                    const itemLink = isCombo ? `/combos/${item.combo_id}` : `/products/${item.product_id}`;
                    const unitPrice = (
                      (parseFloat(item.final_amount) || parseFloat(item.total_price) || 0) /
                      (item.quantity || 1)
                    ).toFixed(2);

                    if (isCombo) {
                      return (
                        <div key={item.id} className="order-item-row order-item-row-combo">
                          <div className="order-combo-top">
                            <div className="order-item-image-wrapper">
                              <img
                                src={getImageUrl(item.product_image)}
                                alt={item.product_name}
                                onError={(e) => {
                                  e.target.src = '/placeholder.png';
                                }}
                              />
                              <span className="order-combo-badge">COMBO</span>
                            </div>
                            <div className="order-item-info">
                              <h4 className="order-item-name">{item.product_name}</h4>
                              <p className="order-combo-subtitle">
                                {item.combo_includes_label ||
                                  comboDetails.map((d) => d.product_name).filter(Boolean).join(' + ') ||
                                  'Combo'}
                                {' · '}
                                {item.quantity} Combo{item.quantity > 1 ? 's' : ''}
                              </p>
                              <p className="order-item-price">₹{unitPrice}</p>
                            </div>
                            <div className="order-item-actions">
                              <Link to={itemLink}>
                                <Button variant="outline-dark" className="view-product-btn">
                                  View Combo
                                </Button>
                              </Link>
                            </div>
                          </div>
                          {comboDetails.length > 0 && (
                            <div className="order-combo-includes">
                              <div className="order-combo-includes-title">
                                This combo includes ({comboDetails.length} Item
                                {comboDetails.length === 1 ? '' : 's'})
                              </div>
                              <div className="order-combo-includes-grid">
                                {comboDetails.map((d, idx) => (
                                  <div key={d.variant_id || idx} className="order-combo-include-card">
                                    <div className="order-combo-include-image">
                                      {d.product_image ? (
                                        <img
                                          src={getImageUrl(d.product_image)}
                                          alt={d.product_name || ''}
                                        />
                                      ) : null}
                                    </div>
                                    <div>
                                      <div className="order-combo-include-name">{d.product_name}</div>
                                      <div className="order-combo-include-variant">
                                        {[d.color_name, d.size].filter(Boolean).join(' • ') || '—'}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div key={item.id} className="order-item-row">
                        <div className="order-item-image-wrapper">
                          <img
                            src={getImageUrl(item.product_image)}
                            alt={item.product_name}
                            onError={(e) => {
                              e.target.src = '/placeholder.png';
                            }}
                          />
                        </div>
                        <div className="order-item-info">
                          <h4 className="order-item-name">
                            {item.product_name}
                            {item.color_name && item.size && (
                              <span className="item-variant">
                                {' '}
                                ({item.size}, {item.color_name}, {item.quantity} Item
                                {item.quantity > 1 ? 's' : ''})
                              </span>
                            )}
                          </h4>
                          <p className="order-item-price">₹{unitPrice}</p>
                        </div>
                        <div className="order-item-actions">
                          <Link to={itemLink}>
                            <Button variant="outline-dark" className="view-product-btn">
                              View Product
                            </Button>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Cost Breakdown */}
                <div className="cost-breakdown">
                  <div className="cost-row">
                    <span>Subtotal:</span>
                    <span className="text-black">₹{(parseFloat(order.amount_refund) || 0).toFixed(2)}</span>
                  </div>
                  <div className="cost-row">
                    <span>Shipping:</span>
                    <span className="text-black">₹{(parseFloat(order.shipping_cost) || 0).toFixed(2)}</span>
                  </div>
                  <div className="cost-row">
                    <span>Taxes (Estimated):</span>
                    <span className="text-black">₹{(parseFloat(order.tax_amount) || 0).toFixed(2)}</span>
                  </div>
                  <div className="cost-row total">
                    <span>Total:</span>
                    <span className="text-black">₹{(parseFloat(order.total_amount) || 0).toFixed(2)}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="address-payment-row">
          {/* Shipping Address */}
          <Col md={6} className="address-col">
            <Card className="address-card">
              <Card.Body>
                <h3 className="section-title">Shipping Address</h3>
                <div className="address-details">
                  <p className="address-name">{order.customer_name}</p>
                  <p className="address-text">{order.shipping_address}</p>
                  {order.shipping_city && order.shipping_state && order.shipping_pincode && (
                    <p className="address-text">
                      {order.shipping_city}, {order.shipping_state} {order.shipping_pincode}
                      {order.shipping_country && `, ${order.shipping_country}`}
                    </p>
                  )}
                  <p className="address-phone">Phone: {order.customer_phone}</p>
                  <p className="address-email">Email: {order.customer_email}</p>
                </div>
              </Card.Body>
            </Card>
        </Col>

          {/* Payment Information */}
          <Col md={6} className="payment-col">
            <Card className="payment-card">
            <Card.Body>
                <h3 className="section-title">Payment Information</h3>
                <div className="payment-details">
                  <div className="payment-item">
                    <span className="payment-label">Payment Method:</span>
                    <div className="payment-value">
                      <PaymentMethodDisplay 
                        paymentMethod={order.payment_method} 
                        paymentMethodDetails={order.payment_method_details}
                      />
                    </div>
                  </div>
                  <div className="payment-item">
                    <span className="payment-label">Payment Status:</span>
                    <span className={`payment-value payment-status-${order.payment_status || 'pending'}`}>
                      {order.payment_status === 'paid' ? 'Paid' : order.payment_status === 'pending' ? 'Pending' : order.payment_status || 'Pending'}
                    </span>
                  </div>
                  {order.payment_id && (
                    <div className="payment-item">
                      <span className="payment-label">Payment ID:</span>
                      <span className="payment-value payment-id">{order.payment_id}</span>
                    </div>
                  )}
                  {order.billing_address && (
                    <div className="payment-item">
                      <span className="payment-label">Billing Address:</span>
                      <span className="payment-value">{order.billing_address}</span>
                    </div>
                  )}
                </div>
            </Card.Body>
          </Card>
          </Col>
        </Row>

        {/* Shipping History */}
        {order.tracking && order.tracking.length > 0 && (
          <Card className="shipping-history-card">
            <Card.Body>
              <h3 className="section-title">Shipping History</h3>
              <div className="shipping-timeline">
                {order.tracking.map((track, index) => (
                  <div key={track.id} className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-date">
                        {new Date(track.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                      <div className="timeline-status">
                        {track.status.charAt(0).toUpperCase() +
                          track.status.slice(1).replace('_', ' ')}
                        {track.notes && `, ${track.notes}`}
                      </div>
                      {track.carrier && <div className="timeline-location">{track.carrier}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Request Return Button */}
        {order.order_status === 'delivered' && (
          <div className="return-button-wrapper">
            <Link to={`/returns/request/${order.id}`}>
              <Button variant="dark" className="request-return-btn">
                Request Return
              </Button>
            </Link>
          </div>
        )}
      </Container>

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Write a Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProduct && (
            <>
              <p>
                <strong>Product:</strong> {selectedProduct.product_name}
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Rating</Form.Label>
                <div>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Button
                      key={star}
                      variant={star <= reviewForm.rating ? 'warning' : 'outline-secondary'}
                      className="me-1"
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    >
                      ★
                    </Button>
                  ))}
                </div>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Review (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={reviewForm.review_text}
                  onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                  placeholder="Share your experience with this product..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleReviewSubmit(selectedProduct?.product_id)}>
            Submit Review
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderDetail;
